import { schema } from 'normalizr';
import { getOrgTermsContent } from '../content/utils.js';
import { stringToMoment } from './utils.js';

export const INITIATIVE_TYPES = [
  'communication',
  'readiness',
  'training',
  'sustainment',
];

export const ORGANIZATION_ROLES = ['admin', 'consultant', 'leader', 'viewer'];
export const CHANGE_INSIGHT_SURVEY_STATUSES = {
  unsent: 'Unsent',
  pending: 'Pending',
  completed: 'Completed',
};

export const PROJECT_ROLES = [
  'viewer',
  'communication leader',
  'readiness leader',
  'training leader',
  'sustainment leader',
  'leader',
  'permissions manager',
  'inviter',
];

export const GROUP_ROLES = ['viewer', 'change advocate'];

export const SUPPORT_FOR_CHANGE = [
  'Very Supportive',
  'Supportive',
  'Neutral',
  'Unsupportive',
  'Very Unsupportive',
];

export const ENGAGEMENT_FREQUENCY = [
  'Very Often',
  'Often',
  'Sometimes',
  'Rarely',
  'Very Rarely',
];

export const PROJECT_ROLES_DESC = [
  getOrgTermsContent('projectRolesDescription1'),
  getOrgTermsContent('projectRolesDescription2'),
  getOrgTermsContent('projectRolesDescription3'),
  getOrgTermsContent('projectRolesDescription4'),
  getOrgTermsContent('projectRolesDescription5'),
  getOrgTermsContent('projectRolesDescription6'),
  getOrgTermsContent('projectRolesDescription7'),
  getOrgTermsContent('projectRolesDescription8'),
];

export const GROUP_ROLES_DESC = [
  getOrgTermsContent('groupRolesDescription1'),
  getOrgTermsContent('groupRolesDescription2'),
];

export const IMPACT_TYPES = [
  'People & Culture',
  'Policies & Procedures',
  'Processes & Workflows',
  'Systems & Technology',
  'Roles & Responsibilities',
  'Teams & Structures',
];

export const IMPACT_ACTION_PLAN_STATUSES = [
  'Not Started',
  'In Progress',
  'Completed',
];

export const IMPACT_ACTION_PLAN_TYPES = [
  'Communication',
  'Readiness',
  'Training',
  'Sustainment',
];

export const IMPACT_SCALE = ['Low', 'Medium', 'High'];

export const PROJECT_IMPACT = ['low', 'medium', 'high'];

export const IMPACT_PERCEPTION = ['Positive', 'Neutral', 'Negative'];

export const TASK_STATUS = ['Not Started', 'In Progress', 'Completed'];

export const RATINGS = {
  HIGH_MEDIUM_LOW: ['high', 'medium', 'low'],
  LARGE_MEDIUM_SMALL: ['large', 'medium', 'small'],
  POSITIVE_NEUTRAL_NEGATIVE: ['positive', 'neutral', 'negative'],
  SUPPORT_LEVELS: [
    'Very Supportive',
    'Supportive',
    'Neutral',
    'Unsupportive',
    'Very Unsupportive',
  ],
  FREQUENCY_LEVELS: [
    'Very Often',
    'Often',
    'Sometimes',
    'Rarely',
    'Very Rarely',
  ],
};

export const REPORT_TYPES = ['pie', 'gantt', 'bar'];

export const GANTT_SCALE_TYPES = ['week', 'month', 'quarter', 'year'];

const datesProcessStrategy = (value) => {
  if (value.hasOwnProperty('startDate')) {
    return {
      ...value,
      startDate: stringToMoment(value.startDate),
      endDate: stringToMoment(value.endDate),
    };
  }

  return {
    ...value,
    start_date: stringToMoment(value.start_date),
    end_date: stringToMoment(value.end_date),
  };
};

export const project = new schema.Entity(
  'projects',
  {},
  {
    processStrategy: datesProcessStrategy,
  }
);

export const projectReport = new schema.Entity(
  'projectReports',
  {
    projects: [project],
  },
  {
    processStrategy: datesProcessStrategy,
  }
);

export const parentPortfolioReport = new schema.Entity(
  'parentPortfolioReports',
  {
    portfolios: [projectReport],
  },
  {
    processStrategy: datesProcessStrategy,
  }
);

export const survey = new schema.Entity(
  'surveys',
  { project },
  {
    processStrategy: (value) => {
      const updatedValue = datesProcessStrategy(value);

      if (!value.sentiments) {
        updatedValue.sentiments = {
          stronglyAgree: 0,
          agree: 0,
          neutral: 0,
          disagree: 0,
          stronglyDisagree: 0,
        };
      }

      return updatedValue;
    },
  }
);

export const initiativeUpload = new schema.Entity('initiativeUploads');

export const initiative = new schema.Entity(
  'initiatives',
  {
    project,
    survey,
    initiativeUpload,
  },
  {
    idAttribute: ({ id, type }) => `${type}${id}`,
    processStrategy: datesProcessStrategy,
  }
);

export const activity = new schema.Entity(
  'activities',
  {},
  {
    processStrategy: datesProcessStrategy,
  }
);

project.define({
  activities: [activity],
  communicationInitiatives: [initiative],
  readinessInitiatives: [initiative],
  trainingInitiatives: [initiative],
  sustainmentInitiatives: [initiative],
});

export const initiativeParticipation = new schema.Entity(
  'initiativeParticipations',
  { initiative },
  {
    processStrategy: (value) => ({
      ...value,
      completedAt: stringToMoment(value.completedAt),
    }),
  }
);

export const initiativeStakeholder = new schema.Entity(
  'stakeholderParticipations',
  { initiative },
  {
    processStrategy: (value) => ({
      ...value,
      completedAt: stringToMoment(value.completedAt),
    }),
  }
);

const employeesDefinition = {
  communicationParticipations: [initiativeParticipation],
  readinessParticipations: [initiativeParticipation],
  trainingParticipations: [initiativeParticipation],
  sustainmentParticipations: [initiativeParticipation],
};

export const employee = new schema.Entity('employees', employeesDefinition); // GET FROM store employee SCHEMA

export const employeeSearch = new schema.Entity(
  'employeesSearch',
  employeesDefinition
); // GET FROM store employee SCHEMA

export const employeeSimplified = new schema.Entity('employeesAutocomplete'); // GET FROM store employee autocomplete SCHEMA

export const employeeNormalize = {
  employees: [new schema.Entity('employees', employeesDefinition)],
}; // PUT IN store employee SCHEMA

export const employeeSearchNormalize = {
  employees: [new schema.Entity('employeesSearch', employeesDefinition)],
}; // PUT IN store employee SCHEMA

export const employeeSimplifiedNormalize = {
  employees: [new schema.Entity('employeesAutocomplete', employeesDefinition)],
}; // PUT IN store employee SCHEMA

export const surveyResponse = new schema.Entity(
  'surveyResponses',
  {
    survey,
    employee,
  },
  {
    processStrategy: (value) => ({
      ...value,
      completedAt: stringToMoment(value.completedAt),
    }),
  }
);

export const surveyResponses = [surveyResponse];

initiativeParticipation.define({
  children: [initiativeParticipation],
  employee,
  surveyResponse,
});

initiativeStakeholder.define({
  children: [initiativeStakeholder],
  employee,
  surveyResponse,
});

export const organization = new schema.Entity('organizations');

export const user = new schema.Entity('users');

export const role = new schema.Entity('roles');

export const file = new schema.Entity('files');

export const template = new schema.Entity('templates');

export const portfolio = new schema.Entity(
  'portfolios',
  {
    projects: [project],
  },
  {
    processStrategy: datesProcessStrategy,
  }
);

export const parentPortfolio = new schema.Entity(
  'parentPortfolios',
  {
    portfolios: [portfolio],
  },
  {
    processStrategy: datesProcessStrategy,
  }
);

export const orgStakeholder = new schema.Entity('orgStakeholders');

export const orgMilestone = new schema.Entity('orgMilestones');

export const orgGroup = new schema.Entity('orgGroups');

export const stakeholder = new schema.Entity('stakeholders');

export const groupParticipation = new schema.Entity('groupParticipations');

export const stakeholderParticipation = new schema.Entity(
  'stakeholderParticipations'
);

export const stakeholderDraft = new schema.Entity('stakeholderDrafts');

export const changeImpact = new schema.Entity('changeImpacts');

export const changeStrategy = new schema.Entity('changeStrategy');

export const task = new schema.Entity('tasks');

export const externalEntity = new schema.Entity('externalEntities');

export const impactedEntities = new schema.Entity('impactedEntities');

export const initiativeCategory = new schema.Entity('initiativeCategories');

export const projectStatuses = new schema.Entity('projectStatuses');

export const projectOwner = new schema.Entity('projectOwners');

export const stakeholderHistory = new schema.Entity('stakeholdersHistory');

export const reportsBarChartItem = new schema.Entity('reportsBarChartItems');

export const surveySettingSchema = new schema.Entity('surveySettings');

export const surveyAnalysis = new schema.Entity('surveyAnalysis');

// Define a schema for the survey response
export const templatesResponseSchema = new schema.Entity('templates');
export const surveyResponseSchema = new schema.Entity('surveyResponses');

// Define a schema for initiative participation
export const initiativeParticipationSchema = new schema.Entity(
  'initiativeParticipationsChangeInsights'
);
export const initiativeGroupParticipationSchema = new schema.Entity(
  'initiativeGroupParticipationsChangeInsights'
);

// Define a schema for the stakeholder participation
export const stakeholderParticipationSchema = new schema.Entity(
  'stakeholderParticipationsChangeInsights'
);

export const initiativeChangeInsightEmployees = new schema.Entity(
  'initiativeEmployees'
);
export const initiativeChangeInsightGroups = new schema.Entity(
  'initiativeGroups'
);
export const initiativeChangeInsightOrganizationStakeholders =
  new schema.Entity('initiativeOrganizationStakeholders');

export const initiativeGroupParticipation = new schema.Entity(
  'initiativeGroupParticipationsChangeInsights',
  { initiative },
  {
    processStrategy: (value) => ({
      ...value,
    }),
  }
);

export const initiativeGroupStakeholders = new schema.Entity(
  'initiativeGroupStakeholdersChangeInsights',
  { initiative },
  {
    processStrategy: (value) => ({
      ...value,
    }),
  }
);

initiativeGroupStakeholders.define({
  children: [initiativeGroupStakeholders],
});

initiativeGroupParticipation.define({
  children: [initiativeGroupParticipation],
});

export const initiativeRelationsSchema = {
  employees: [initiativeChangeInsightEmployees],
  groups: [initiativeChangeInsightGroups],
  organizationStakeholders: [initiativeChangeInsightOrganizationStakeholders],
};

// Define a schema for the survey entity
export const surveySchema = {
  groupedInitiativeParticipations: [initiativeGroupParticipation],
  groupedStakeholderParticipations: [initiativeGroupStakeholders],
  initiativeParticipations: [initiativeParticipationSchema],
  stakeholderParticipations: [stakeholderParticipationSchema],
  surveyResponses: [surveyResponseSchema],
  survey: surveySettingSchema,
};

export const changeImpactType = new schema.Entity('changeImpactTypes');

export const formParticipantSchema = new schema.Entity('groups', {
  employeeIds: [new schema.Entity('employees')],
});

export const changeImpactParticipants = new schema.Entity(
  'changeImpactParticipants'
);

export const projectDashboard = new schema.Entity(
  'projectDashboards',
  {},
  {
    idAttribute: 'projectId',
  }
);
